import Auth from '@aws-amplify/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject, BehaviorSubject, of, from } from 'rxjs';
import { map, catchError, distinctUntilChanged } from 'rxjs/operators';
import { get, cloneDeep } from 'lodash';
import Cookies from 'js-cookie';
import { __assign } from 'tslib';
import { Router, ActivatedRoute } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { Injectable, Inject, NgModule, CUSTOM_ELEMENTS_SCHEMA, Component, defineInjectable, inject } from '@angular/core';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var InstitutionService = /** @class */ (function () {
    function InstitutionService(http, environment) {
        this.http = http;
        this.environment = environment;
    }
    /**
     * @private
     * @param {?} type
     * @return {?}
     */
    InstitutionService.prototype.getPortalType = /**
     * @private
     * @param {?} type
     * @return {?}
     */
    function (type) {
        return type.match(/legacy|leg/) ? 'leg' : 'phx';
    };
    /**
     * @private
     * @param {?} institutions
     * @return {?}
     */
    InstitutionService.prototype.setupData = /**
     * @private
     * @param {?} institutions
     * @return {?}
     */
    function (institutions) {
        var _this = this;
        /** @type {?} */
        var data = institutions.map((/**
         * @param {?} data
         * @param {?} index
         * @return {?}
         */
        function (data, index) {
            /** @type {?} */
            var portal = _this.getPortalType(data.portal);
            /** @type {?} */
            var provider = data.loginType === 'saml' ? portal + "." + data.id : 'COGNITO';
            return __assign({}, data, { uid: index, id: data.id, name: data.name + " (" + data.code + ")", institution: data.name, portal: portal,
                provider: provider });
        }));
        return Promise.resolve(data);
    };
    /**
     * @private
     * @return {?}
     */
    InstitutionService.prototype.institutionData = /**
     * @private
     * @return {?}
     */
    function () {
        if (this.environment['institution']) {
            return Promise.resolve(this.environment['institution']);
        }
        else {
            return this.http.get(this.environment.urls.institutionApiUrl, {}).toPromise();
        }
    };
    /**
     * @param {?} id
     * @param {?} portal
     * @return {?}
     */
    InstitutionService.prototype.search = /**
     * @param {?} id
     * @param {?} portal
     * @return {?}
     */
    function (id, portal) {
        return this.get().then((/**
         * @param {?} results
         * @return {?}
         */
        function (results) {
            /** @type {?} */
            var data = results.filter((/**
             * @param {?} data
             * @return {?}
             */
            function (data) { return data.id === id && data.portal === portal; }));
            return data && data[0];
        }));
    };
    /**
     * @param {?} uuid
     * @return {?}
     */
    InstitutionService.prototype.searchByUuid = /**
     * @param {?} uuid
     * @return {?}
     */
    function (uuid) {
        return this.get().then((/**
         * @param {?} results
         * @return {?}
         */
        function (results) {
            /** @type {?} */
            var data = results.filter((/**
             * @param {?} data
             * @return {?}
             */
            function (data) { return data.uuid === uuid; }));
            return data && data[0];
        }));
    };
    /**
     * @return {?}
     */
    InstitutionService.prototype.get = /**
     * @return {?}
     */
    function () {
        var _this = this;
        if (!this.list) {
            this.list = this.institutionData().then((/**
             * @param {?} data
             * @return {?}
             */
            function (data) { return _this.setupData(data); }));
            return this.list;
        }
        else {
            return this.list.then((/**
             * @param {?} results
             * @return {?}
             */
            function (results) { return results; }));
        }
    };
    InstitutionService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    InstitutionService.ctorParameters = function () { return [
        { type: HttpClient },
        { type: undefined, decorators: [{ type: Inject, args: ['environment',] }] }
    ]; };
    return InstitutionService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var AuthUtil = /** @class */ (function () {
    function AuthUtil(env) {
        this.env = env;
        var _a = this.env.aws.Auth.cookieStorage, path = _a.path, domain = _a.domain, secure = _a.secure;
        this.cookieConfig = {
            path: path,
            domain: domain,
            secure: secure
        };
        if (secure) {
            this.cookieConfig['sameSite'] = 'None';
        }
    }
    /**
     * @param {?} name
     * @return {?}
     */
    AuthUtil.prototype.getCookie = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        /** @type {?} */
        var data = Cookies.get(name, this.cookieConfig);
        return data && JSON.parse(data);
    };
    /**
     * @param {?} name
     * @param {?} value
     * @return {?}
     */
    AuthUtil.prototype.setCookie = /**
     * @param {?} name
     * @param {?} value
     * @return {?}
     */
    function (name, value) {
        /** @type {?} */
        var data = JSON.stringify(value);
        return Cookies.set(name, data, this.cookieConfig);
    };
    /**
     * @param {?} name
     * @return {?}
     */
    AuthUtil.prototype.deleteCookie = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        return Cookies.remove(name, this.cookieConfig);
    };
    /**
     * @param {?} user
     * @param {?} route
     * @return {?}
     */
    AuthUtil.prototype.permission = /**
     * @param {?} user
     * @param {?} route
     * @return {?}
     */
    function (user, route) {
        /** @type {?} */
        var roles = get(route, 'data.permitted');
        return roles && roles.indexOf(user['custom:role']) > -1;
    };
    /**
     * @param {?} username
     * @return {?}
     */
    AuthUtil.prototype.formatUsername = /**
     * @param {?} username
     * @return {?}
     */
    function (username) {
        var _a = this.getProvider(), id = _a.id, portal = _a.portal;
        /** @type {?} */
        var user;
        if (portal === 'phx' && !username.match('@')) {
            user = username;
        }
        else {
            user = username.toLowerCase();
        }
        return user + "@@" + id + "." + portal;
    };
    /**
     * @return {?}
     */
    AuthUtil.prototype.getReferrer = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var ref = this.getCookie('es-referrer');
        this.deleteCookie('es-referrer');
        return ref;
    };
    /**
     * @return {?}
     */
    AuthUtil.prototype.saveReferrer = /**
     * @return {?}
     */
    function () {
        var _this = this;
        return new Promise((/**
         * @param {?} resolve
         * @param {?} reject
         * @return {?}
         */
        function (resolve, reject) {
            /** @type {?} */
            var loc = window.location.href;
            _this.setCookie('es-referrer', loc);
            resolve(true);
        }));
    };
    /**
     * @return {?}
     */
    AuthUtil.prototype.getProvider = /**
     * @return {?}
     */
    function () {
        return this.getCookie('es-provider') || this.getCookie('temp-es-provider');
    };
    /**
     * @return {?}
     */
    AuthUtil.prototype.setProvider = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var institution = this.getCookie("temp-es-provider");
        if (institution) {
            this.deleteCookie('temp-es-provider');
            this.setCookie('es-provider', institution);
        }
    };
    /**
     * @return {?}
     */
    AuthUtil.prototype.removeProvider = /**
     * @return {?}
     */
    function () {
        this.deleteCookie('temp-es-provider');
        this.deleteCookie('es-provider');
    };
    /**
     * @param {?} result
     * @return {?}
     */
    AuthUtil.prototype.cleanData = /**
     * @param {?} result
     * @return {?}
     */
    function (result) {
        /** @type {?} */
        var data = result && (result.idToken || result.signInUserSession.idToken);
        /** @type {?} */
        var details = data.payload;
        /** @type {?} */
        var fullName = details.given_name && details.family_name ? details.given_name + " " + details.family_name : '';
        return __assign({}, details, { jwtToken: data.jwtToken, fullName: fullName, username: result.username, role: details['custom:role'], esId: details['custom:es-id'] });
    };
    /**
     * @param {?=} type
     * @return {?}
     */
    AuthUtil.prototype.isHosted = /**
     * @param {?=} type
     * @return {?}
     */
    function (type) {
        if (type === void 0) { type = null; }
        /** @type {?} */
        var data;
        if (type === 'main') {
            data = this.getCookie('es-provider');
        }
        else if (type === 'temp') {
            data = this.getCookie('temp-es-provider');
        }
        else {
            data = this.getCookie('es-provider') || this.getCookie('temp-es-provider');
        }
        return data && data.provider !== 'COGNITO';
    };
    /**
     * @return {?}
     */
    AuthUtil.prototype.providerDelay = /**
     * @return {?}
     */
    function () {
        return this.isHosted() ? 800 : 0;
    };
    /**
     * @return {?}
     */
    AuthUtil.prototype.isPortalLogin = /**
     * @return {?}
     */
    function () {
        return Cookies.get('esLoginData');
    };
    AuthUtil.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    AuthUtil.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: ['environment',] }] }
    ]; };
    return AuthUtil;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var AuthService = /** @class */ (function () {
    function AuthService(environment, institutionService, http, route, router, util) {
        this.environment = environment;
        this.institutionService = institutionService;
        this.http = http;
        this.route = route;
        this.router = router;
        this.util = util;
        this.user$ = new BehaviorSubject(false);
        if (this.configure()) {
            this.loggedIn = new Subject();
            this.ready();
        }
    }
    /**
     * @return {?}
     */
    AuthService.prototype.init = /**
     * @return {?}
     */
    function () {
        var _this = this;
        /** @type {?} */
        var result = this.loggedIn.pipe(distinctUntilChanged());
        result.subscribe((/**
         * @param {?} data
         * @return {?}
         */
        function (data) {
            /** @type {?} */
            var loc = window.location.href;
            if (!data) {
                if (_this.environment.esLogin === loc + "/login") {
                    _this.router.navigate(['/login']);
                }
                else if (loc != _this.environment.esLogin) {
                    _this.util.saveReferrer().then((/**
                     * @return {?}
                     */
                    function () {
                        window.location.href = _this.environment.esLogin;
                    }));
                }
            }
            else {
                _this.util.deleteCookie('esLoginData');
            }
        }));
        return result;
    };
    /**
     * @private
     * @return {?}
     */
    AuthService.prototype.configure = /**
     * @private
     * @return {?}
     */
    function () {
        /** @type {?} */
        var env = this.environment;
        if (env.aws.Auth && env.aws.oauth) {
            this.esLogin = env.esLogin;
            this.awsEnvCognito = cloneDeep(env.aws);
            this.clientId = env.aws.Auth.userPoolWebClientId;
            this.oauth = env.aws.oauth;
            if (this.util.isPortalLogin()) {
                delete this.awsEnvCognito.Auth.authenticationFlowType;
                delete this.awsEnvCognito.oauth;
            }
            else if (!this.util.isHosted()) {
                delete this.awsEnvCognito.oauth;
            }
            Auth.configure(this.awsEnvCognito);
            return true;
        }
        else {
            console.error('AWS Settings and institutionApiUrl required in environment.ts!');
            return false;
        }
    };
    /**
     * @private
     * @return {?}
     */
    AuthService.prototype.ready = /**
     * @private
     * @return {?}
     */
    function () {
        var _this = this;
        setTimeout((/**
         * @return {?}
         */
        function () {
            Auth.currentAuthenticatedUser()
                .then((/**
             * @param {?} data
             * @return {?}
             */
            function (data) {
                _this.util.setProvider();
                _this.userReady(data);
            }))
                .catch((/**
             * @return {?}
             */
            function () {
                _this.loggedIn.next(false);
            }));
        }), this.util.providerDelay());
    };
    /**
     * @param {?} route
     * @param {?} state
     * @return {?}
     */
    AuthService.prototype.guard = /**
     * @param {?} route
     * @param {?} state
     * @return {?}
     */
    function (route, state) {
        var _this = this;
        return from(Auth.currentAuthenticatedUser())
            .pipe(map((/**
         * @param {?} result
         * @return {?}
         */
        function (result) {
            _this.userReady(result);
            _this.goAway(route, result);
            return true;
        })), catchError((/**
         * @param {?} error
         * @return {?}
         */
        function (error) {
            _this.destroyUser();
            return of(false);
        })));
    };
    /**
     * @private
     * @param {?} route
     * @param {?} loggedIn
     * @return {?}
     */
    AuthService.prototype.goAway = /**
     * @private
     * @param {?} route
     * @param {?} loggedIn
     * @return {?}
     */
    function (route, loggedIn) {
        if (this.user && this.user['custom:role']) {
            /** @type {?} */
            var notPermitted = !this.util.permission(this.user, route);
            this.routing && this.routing(notPermitted);
        }
        else {
            this.router.navigate(['/forbidden']);
        }
    };
    /**
     * @return {?}
     */
    AuthService.prototype.switchInstitution = /**
     * @return {?}
     */
    function () {
        this.util.removeProvider();
        this.logout();
    };
    /**
     * @return {?}
     */
    AuthService.prototype.isAdmin = /**
     * @return {?}
     */
    function () {
        return this.user && this.user['custom:role'] === 'admin';
    };
    /**
     * @private
     * @return {?}
     */
    AuthService.prototype.destroyUser = /**
     * @private
     * @return {?}
     */
    function () {
        this.user = null;
        this.user$.next(false);
        this.loggedIn.next(false);
    };
    /**
     * @private
     * @param {?} result
     * @return {?}
     */
    AuthService.prototype.userReady = /**
     * @private
     * @param {?} result
     * @return {?}
     */
    function (result) {
        /** @type {?} */
        var data = this.util.cleanData(result);
        this.user = data;
        this.user$.next(data);
        this.loggedIn.next(true);
    };
    /**
     * @return {?}
     */
    AuthService.prototype.hosted = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var institution = this.util.getProvider();
        Auth.configure(this.environment['aws']['oauth']);
        if (institution) {
            /** @type {?} */
            var oauth = this.oauth;
            /** @type {?} */
            var url = 'https://'
                + oauth.domain
                + '/oauth2/authorize?redirect_uri=' + encodeURI(oauth.redirectSignIn)
                + '&response_type=' + oauth.responseType
                + '&scope=' + oauth.scope.join('+')
                + '&client_id=' + this.clientId
                + '&identity_provider=' + institution.provider;
            window.location.assign(url);
        }
        else {
            window.location.assign(this.environment.esLogin);
        }
    };
    /**
     * @param {?} username
     * @param {?} password
     * @return {?}
     */
    AuthService.prototype.cognito = /**
     * @param {?} username
     * @param {?} password
     * @return {?}
     */
    function (username, password) {
        var _this = this;
        this.util.setProvider();
        this.configure();
        /** @type {?} */
        var signInName = this.util.formatUsername(username);
        return new Promise((/**
         * @param {?} resolve
         * @param {?} reject
         * @return {?}
         */
        function (resolve, reject) {
            Auth.signIn(signInName, password)
                .then((/**
             * @param {?} data
             * @return {?}
             */
            function (data) {
                _this.userReady(data);
                resolve(data);
            }))
                .catch((/**
             * @param {?} err
             * @return {?}
             */
            function (err) {
                reject(err);
            }));
        }));
    };
    /**
     * @return {?}
     */
    AuthService.prototype.tokenLogin = /**
     * @return {?}
     */
    function () {
        var _this = this;
        /** @type {?} */
        var loginData = JSON.parse(this.util.isPortalLogin());
        var userData = loginData.userData;
        /** @type {?} */
        var schoolInfo = userData.uuid ?
            this.institutionService.searchByUuid(userData.uuid) :
            this.institutionService.search(userData.institutionId * 1, userData.portal);
        this.util.deleteCookie('esLoginData');
        delete userData.role;
        return schoolInfo.then((/**
         * @param {?} institution
         * @return {?}
         */
        function (institution) {
            if (institution) {
                _this.util.setCookie('temp-es-provider', institution);
                if (institution.loginType.match(/ldap|saml/)) {
                    return _this.genericLogin(userData.userId, institution, loginData)
                        .then((/**
                     * @param {?} data
                     * @return {?}
                     */
                    function (data) {
                        if (data.result) {
                            /** @type {?} */
                            var newEmail = data.result;
                            return _this.sendToCustomChallenge(newEmail, loginData);
                        }
                        else {
                            return Promise.reject('Generic User Error');
                        }
                    }));
                }
                else {
                    /** @type {?} */
                    var email = _this.util.formatUsername(userData.userId);
                    return _this.sendToCustomChallenge(email, loginData);
                }
            }
            else {
                return Promise.reject('Institution Not Found');
            }
        })).catch((/**
         * @param {?} err
         * @return {?}
         */
        function (err) {
            _this.configure();
            return Promise.reject(err);
        }));
    };
    /**
     * @private
     * @param {?} userId
     * @param {?} institution
     * @param {?} loginData
     * @return {?}
     */
    AuthService.prototype.genericLogin = /**
     * @private
     * @param {?} userId
     * @param {?} institution
     * @param {?} loginData
     * @return {?}
     */
    function (userId, institution, loginData) {
        var _this = this;
        /** @type {?} */
        var type = institution.loginType;
        if (type.match(/ldap|saml/)) {
            /** @type {?} */
            var data = __assign({}, loginData);
            var token = data.token;
            /** @type {?} */
            var headers = new HttpHeaders({
                'Authorization': token,
                'Content-Type': 'application/json'
            });
            data.userData['loginType'] = type;
            return this.http.post(this.environment.urls.cogApiUrl + "/generic-user", data, { headers: headers })
                .toPromise()
                .then((/**
             * @return {?}
             */
            function () {
                var _a = _this.util.getProvider(), id = _a.id, portal = _a.portal;
                /** @type {?} */
                var newUser = userId + "@@" + id + "." + portal;
                return { result: "generic__" + newUser };
            }));
        }
        else {
            return Promise.resolve({ result: null });
        }
    };
    /**
     * @private
     * @param {?} email
     * @param {?} loginData
     * @return {?}
     */
    AuthService.prototype.sendToCustomChallenge = /**
     * @private
     * @param {?} email
     * @param {?} loginData
     * @return {?}
     */
    function (email, loginData) {
        var _this = this;
        return Auth.signIn(email).then((/**
         * @param {?} user
         * @return {?}
         */
        function (user) {
            if (user.challengeName === 'CUSTOM_CHALLENGE') {
                return Auth.sendCustomChallengeAnswer(user, JSON.stringify({ challengeResponse: loginData }))
                    .then((/**
                 * @param {?} user
                 * @return {?}
                 */
                function (user) {
                    return Auth.currentAuthenticatedUser().then((/**
                     * @param {?} user
                     * @return {?}
                     */
                    function (user) {
                        _this.util.setProvider();
                        return Auth.currentSession().then((/**
                         * @param {?} data
                         * @return {?}
                         */
                        function (data) {
                            _this.userReady(data);
                            return data;
                        }));
                    }));
                }));
            }
        }));
    };
    /**
     * @return {?}
     */
    AuthService.prototype.logout = /**
     * @return {?}
     */
    function () {
        var _this = this;
        return Auth.signOut()
            .then((/**
         * @return {?}
         */
        function () {
            _this.destroyUser();
        }))
            .catch((/**
         * @param {?} err
         * @return {?}
         */
        function (err) { return console.log(err); }));
    };
    /**
     * @return {?}
     */
    AuthService.prototype.getJwt = /**
     * @return {?}
     */
    function () {
        // TODO: if no jwtToken?
        return this.user.jwtToken;
    };
    AuthService.decorators = [
        { type: Injectable, args: [{
                    providedIn: 'root'
                },] }
    ];
    /** @nocollapse */
    AuthService.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: ['environment',] }] },
        { type: InstitutionService },
        { type: HttpClient },
        { type: ActivatedRoute },
        { type: Router },
        { type: AuthUtil }
    ]; };
    /** @nocollapse */ AuthService.ngInjectableDef = defineInjectable({ factory: function AuthService_Factory() { return new AuthService(inject("environment"), inject(InstitutionService), inject(HttpClient), inject(ActivatedRoute), inject(Router), inject(AuthUtil)); }, token: AuthService, providedIn: "root" });
    return AuthService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var EsLoginComponent = /** @class */ (function () {
    function EsLoginComponent(authService, institutionService, route, router, util) {
        var _this = this;
        this.authService = authService;
        this.institutionService = institutionService;
        this.route = route;
        this.router = router;
        this.util = util;
        this.currentInstitution = null;
        this.institutions = [];
        this.cognitoLogin = false;
        this.isPWA = window.matchMedia('(display-mode: standalone)').matches;
        this.timeLeft = 3;
        this.loggingIn = false;
        this.loadInstitution();
        if (this.util.isPortalLogin()) {
            this.authService.tokenLogin().catch((/**
             * @param {?} err
             * @return {?}
             */
            function (err) {
                _this.setup();
            }));
        }
        else {
            /** @type {?} */
            var pro = this.util.getProvider();
            if (pro && !pro.uid) {
                this.setupFormByInstitution(pro.id, pro.portal);
            }
            else {
                this.setup();
            }
        }
    }
    /**
     * @return {?}
     */
    EsLoginComponent.prototype.waitForForm = /**
     * @return {?}
     */
    function () {
        return !(this.showForm && this.showForm.match(/institution|redirect|cognito/));
    };
    /**
     * @private
     * @param {?} institutionId
     * @param {?} portal
     * @return {?}
     */
    EsLoginComponent.prototype.setupFormByInstitution = /**
     * @private
     * @param {?} institutionId
     * @param {?} portal
     * @return {?}
     */
    function (institutionId, portal) {
        var _this = this;
        this.institutionService.search(institutionId, portal).then((/**
         * @param {?} data
         * @return {?}
         */
        function (data) {
            _this.util.removeProvider();
            _this.goToLogin(__assign({}, data));
        }));
    };
    /**
     * @private
     * @return {?}
     */
    EsLoginComponent.prototype.setup = /**
     * @private
     * @return {?}
     */
    function () {
        /** @type {?} */
        var savedInstitution = this.util.getProvider();
        if (savedInstitution) {
            if (savedInstitution.loginType === 'saml') {
                this.showForm = 'redirect';
                this.waitForCancellation();
            }
            else {
                this.createForm();
            }
        }
        else {
            if (this.institutions.length === 1) {
                this.goToLogin(this.institutions[0]);
            }
            else {
                this.showForm = 'institution';
            }
        }
    };
    /**
     * @param {?} $event
     * @return {?}
     */
    EsLoginComponent.prototype.selectInstitution = /**
     * @param {?} $event
     * @return {?}
     */
    function ($event) {
        if ($event.detail && $event.detail.uid >= 0) {
            /** @type {?} */
            var index = $event.detail.uid;
            this.selection = this.institutions[index];
        }
        else if ($event.detail.reset) {
            this.selection = null;
        }
    };
    /**
     * @private
     * @return {?}
     */
    EsLoginComponent.prototype.loadInstitution = /**
     * @private
     * @return {?}
     */
    function () {
        var _this = this;
        return this.institutionService.get().then((/**
         * @param {?} response
         * @return {?}
         */
        function (response) {
            _this.institutions = response;
        }));
    };
    /**
     * @param {?} data
     * @return {?}
     */
    EsLoginComponent.prototype.showErrors = /**
     * @param {?} data
     * @return {?}
     */
    function (data) {
        return data && data.invalid && data.dirty;
    };
    /**
     * @private
     * @return {?}
     */
    EsLoginComponent.prototype.waitForCancellation = /**
     * @private
     * @return {?}
     */
    function () {
        var _this = this;
        this.timerInterval = setInterval((/**
         * @return {?}
         */
        function () {
            if (_this.timeLeft === 1) {
                clearInterval(_this.timerInterval);
                _this.goToLogin(_this.util.getProvider());
            }
            else {
                _this.timeLeft--;
            }
        }), 1000);
    };
    /**
     * @private
     * @return {?}
     */
    EsLoginComponent.prototype.createForm = /**
     * @private
     * @return {?}
     */
    function () {
        this.showForm = 'cognito';
        /** @type {?} */
        var data = this.util.getProvider();
        this.currentInstitution = data && data.name;
        this.loginForm = new FormGroup({
            username: new FormControl('', { validators: [Validators.required] }),
            password: new FormControl('', { validators: Validators.required })
        });
    };
    /**
     * @return {?}
     */
    EsLoginComponent.prototype.allowInstitutionSelection = /**
     * @return {?}
     */
    function () {
        clearInterval(this.timerInterval);
        this.util.removeProvider();
        this.showForm = 'institution';
    };
    /**
     * @return {?}
     */
    EsLoginComponent.prototype.chooseInstitution = /**
     * @return {?}
     */
    function () {
        if (this.selection) {
            this.goToLogin(this.selection);
        }
    };
    /**
     * @private
     * @param {?} institution
     * @return {?}
     */
    EsLoginComponent.prototype.goToLogin = /**
     * @private
     * @param {?} institution
     * @return {?}
     */
    function (institution) {
        this.util.setCookie("temp-es-provider", institution);
        if (institution.provider === 'COGNITO') {
            clearInterval(this.timerInterval);
            this.createForm();
        }
        else {
            this.authService.hosted();
        }
    };
    /**
     * @private
     * @param {?} error
     * @return {?}
     */
    EsLoginComponent.prototype.displayError = /**
     * @private
     * @param {?} error
     * @return {?}
     */
    function (error) {
        /** @type {?} */
        var output = error.message;
        switch (error.code) {
            case 'UserNotFoundException':
                output = 'Please confirm your selected institution, username and password are correct.';
                break;
        }
        return output;
    };
    /**
     * @return {?}
     */
    EsLoginComponent.prototype.onSubmitCognito = /**
     * @return {?}
     */
    function () {
        var _this = this;
        var _a = this.loginForm.value, username = _a.username, password = _a.password;
        this.loggingIn = true;
        this.authService
            .cognito(username, password)
            .then((/**
         * @return {?}
         */
        function () {
        }))
            .catch((/**
         * @param {?} error
         * @return {?}
         */
        function (error) {
            _this.loggingIn = false;
            _this.loginForm.reset();
            _this.loginForm.setErrors(_this.displayError(error));
        }));
    };
    EsLoginComponent.decorators = [
        { type: Component, args: [{
                    selector: 'es-login',
                    template: "<es-card class=\"f-col f-jcc f-aic\">\n\n  <div class=\"logo\">\n    <ng-content></ng-content>\n  </div>\n  <es-spinner *ngIf=\"waitForForm()\" bg=\"dark\"></es-spinner>\n\n  <div class=\"form-element f-col f-jfs\" *ngIf=\"showForm === 'institution'\">\n    <label for=\"institution\">Institution</label>\n    <!-- TODO: set the input to disabled instead of spinner to avoid blank space before dropdown renders -->\n\n    <es-complete\n      id=\"institution\"\n      *ngIf=\"institutions.length > 0\"\n      [items]=\"institutions\"\n      placeholder=\"Choose your Institution\"\n      (esCompleteSubmit)=\"selectInstitution($event)\">\n    </es-complete>\n    <es-spinner *ngIf=\"institutions.length === 0\" bg=\"dark\"></es-spinner>\n    <button type=\"button\" class=\"progressive\" [disabled]=\"!selection\" (click)=\"chooseInstitution()\">Continue</button>\n  </div>\n\n  <ng-container class=\"tac\" *ngIf=\"showForm === 'redirect'\">\n    <h3>Redirecting to login in {{timeLeft}} seconds.</h3>\n    <button class=\"progressive\" type=\"submit\" (click)=\"allowInstitutionSelection()\">Choose Institution</button>\n  </ng-container>\n\n  <ng-container *ngIf=\"showForm === 'cognito'\">\n    <div class=\"form-element\">\n      <label id=\"institutionName\">Institution</label>\n      <div aria-describedby=\"institutionName\">{{this.currentInstitution}}</div>\n    </div>\n\n    <form class=\"f-col\" (ngSubmit)=\"onSubmitCognito()\" [formGroup]=\"loginForm\">\n      <div class=\"form-element f-col f-jfs\">\n        <label for=\"username\">Login</label>\n        <input type=\"text\" autocomplete=\"username\" name=\"username\" id=\"username\" placeholder=\"username or email\" formControlName=\"username\">\n        <span class=\"error\" *ngIf=\"showErrors(loginForm.controls.username)\">Username or Email Required</span>\n      </div>\n      <div class=\"form-element f-col f-jfs\">\n        <label for=\"password\">Password</label>\n        <input type=\"password\" autocomplete=\"password\" name=\"password\" id=\"password\" placeholder=\"enter password\" formControlName=\"password\">\n        <span class=\"error\" *ngIf=\"showErrors(loginForm.controls.password)\">Password Required</span>\n      </div>\n      <div class=\"form-element f-col f-jfs\">\n        <button type=\"submit\" class=\"action\" [disabled]=\"loginForm?.invalid || loggingIn\">\n          <span *ngIf=\"!loggingIn\">Submit</span>\n          <span *ngIf=\"loggingIn\">Logging In</span>\n        </button>\n        <span class=\"error\" *ngIf=\"loginForm?.invalid\">{{loginForm?.errors}}</span>\n      </div>\n      <button class=\"text\" (click)=\"allowInstitutionSelection()\">Switch Institution</button>\n    </form>\n  </ng-container>\n</es-card>\n",
                    styles: ["es-card{position:relative;z-index:3}es-card .logo{margin:.5rem 0}es-card .form-element,es-card es-complete{margin-bottom:1rem}es-card button{margin-top:.5rem}"]
                }] }
    ];
    /** @nocollapse */
    EsLoginComponent.ctorParameters = function () { return [
        { type: AuthService },
        { type: InstitutionService },
        { type: ActivatedRoute },
        { type: Router },
        { type: AuthUtil }
    ]; };
    return EsLoginComponent;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var EsLoginModule = /** @class */ (function () {
    function EsLoginModule() {
    }
    /**
     * @param {?} environment
     * @return {?}
     */
    EsLoginModule.forRoot = /**
     * @param {?} environment
     * @return {?}
     */
    function (environment) {
        return {
            ngModule: EsLoginModule,
            providers: [
                {
                    provide: 'environment',
                    useValue: environment
                }
            ]
        };
    };
    EsLoginModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [EsLoginComponent],
                    imports: [
                        BrowserModule,
                        FormsModule,
                        ReactiveFormsModule
                    ],
                    providers: [
                        InstitutionService,
                        AuthUtil
                    ],
                    exports: [EsLoginComponent],
                    schemas: [
                        CUSTOM_ELEMENTS_SCHEMA
                    ]
                },] }
    ];
    return EsLoginModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { AuthService, AuthUtil, InstitutionService, EsLoginComponent, EsLoginModule };

//# sourceMappingURL=examsoft-es-login.js.map